import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import classes from "./RosterConfig.module.css";
import Grid from "@mui/material/Grid";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  getRosterConfig,
  createRosterConfig,
  updateRosterConfiguration,
} from "../../services/userServices";
import { rosterConfigAction } from "../../Store/RosterConfig";
import Inputs from "../../components/RosterConfigComponents/ConfigInputs/ConfigInputs";
import TraineeDetails from "../../components/RosterConfigComponents/ConfigTraineeDetails/TraineeDetails";
import AsttButton from "components/AsttButton/AsttButton";
import AsttButtonLight from "components/ASTTButtonLight/AsttButtonLight";
import BackdropComponent from "../../components/Spinner/Spinner";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import { SnackBarStoreAction } from "Store/SnackBar";
import applicationUsageLog from "../../utils/applicationUsagesLog";

const RosterConfig = () => {
  const { classRosterId, classRequestId, type: typeParam, data } = useParams();
  const { t } = useTranslation(["rosterConfig"]);
  const location = window.location.pathname.split("/");
  const redirectlocation = useLocation();
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const rosterConfig = useSelector((state) => state.rosterConfig);
  const classRequestIdStore = useSelector((state) => state.generalStore);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [type, setType] = useState("clone");

  const [loading, setLoading] = useState([false]);

  useEffect(() => {
    applicationUsageLog(
      localStorage.getItem("User"),
      localStorage.getItem("EmailId"),
      "ASTT",
      "Roster Config",
    );
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!cancelled) {
        setLoading(true);
      }

      try {
        if (classRosterId !== "null") {
          if (!cancelled) {
            setType(typeParam);
          }

          const configRosterDetails = await getRosterConfig(
            classRosterId,
            data,
          );

          if (cancelled) return;

          if (configRosterDetails.status === true) {
            if (!cancelled) {
              dispatchClassRosterData(configRosterDetails.data[0]);
            }
          }
        }

        if (!cancelled) {
          setType(typeParam);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch roster config:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [classRosterId, typeParam, data]);

  const Snackhandler = (open, message, severity) => {
    const obj = {
      open: open,
      message: message,
      severity: severity,
    };
    dispatch(SnackBarStoreAction.updateSnack(obj));
  };

  const getrosterConfigDateTimes = () => {
    const accDate = rosterConfig.accna ? null : rosterConfig.accDate;
    const nestingDate = rosterConfig.nestingNA
      ? null
      : rosterConfig.nestingDate;
    const nestingEndDate = rosterConfig.nestingNA
      ? null
      : rosterConfig.nestingEndDate;
    const productTrainingDate = rosterConfig.productTrainingNA
      ? null
      : rosterConfig.productTrainingDate;
    const tciDate = rosterConfig.tcina ? null : rosterConfig.tciDate;
    const tciRecoveryDate = rosterConfig.tciRecoveryNA
      ? null
      : rosterConfig.tciRecoveryDate;
    const techTestDate = rosterConfig.techTestNA
      ? null
      : rosterConfig.ttdBotDate;
    const techTestRecoveryDate = rosterConfig.techTestRecoveryNA
      ? null
      : rosterConfig.ttdRecoveryDate;
    const ttfDate = rosterConfig.ttfna ? null : rosterConfig.ttfDate;
    return {
      accDate,
      nestingDate,
      nestingEndDate,
      productTrainingDate,
      tciDate,
      tciRecoveryDate,
      techTestDate,
      techTestRecoveryDate,
      ttfDate,
    };
  };

  const preparePayloadForCreate = () => {
    const {
      accDate,
      nestingDate,
      nestingEndDate,
      productTrainingDate,
      tciDate,
      tciRecoveryDate,
      techTestDate,
      techTestRecoveryDate,
      ttfDate,
    } = getrosterConfigDateTimes();
    return {
      classRequestId: classRequestId,
      wave: rosterConfig.wave,
      modifiedBy: localStorage.getItem("User"),
      applyEditToAllTrainees: rosterConfig.applyAll ? 1 : 0,
      classRosterDetail: {
        techEquipmentSolutionId: rosterConfig.techEquipmentSolution.toString(),
        ACCDate: accDate?.date,
        ACCTime: accDate?.time,
        ClientSpecificZoomHostKey: rosterConfig.clientSpecificZoomHostKey,
        ClientSpecificZoomLink: rosterConfig.clientSpecificLink,
        ClientSpecificZoomPassword: rosterConfig.clientSpecificPw,
        IDMTicketNumber: rosterConfig.rosterIdm,
        NestingDate: nestingDate?.date,
        ACCNA: rosterConfig.accna,
        notes: rosterConfig.notes,
        NestingEndDate: nestingEndDate?.date,
        NestingEndTime: nestingEndDate?.time,
        NestingNA: rosterConfig.nestingNA,
        NestingTime: nestingDate?.time,
        ProductTrainingDate: productTrainingDate?.date,
        ProductTrainingTime: productTrainingDate?.time,
        ProductTrainingZoomLink: rosterConfig.productionTrainingZoomLink,
        ProductTrainingZoomPassword: rosterConfig.productionTrainingZoomPw,
        ProductionDate: rosterConfig.productionStartDate.date,
        SRWDownloadDueDate: rosterConfig.srw.date,
        SRWDownloadDueTime: rosterConfig.srw.time,
        TCIDate: tciDate?.date,
        TCINA: rosterConfig.tcina,
        TCIRecoveryDate: tciRecoveryDate?.date,
        TCIRecoveryNA: rosterConfig.tciRecoveryNA,
        TCIRecoveryTime: tciRecoveryDate?.time,
        TCIRecoveryZoomLink: rosterConfig.tciRecoveryZoomLink,
        TCIRecoveryZoomPassword: rosterConfig.tciRecoveryZoomPw,
        TCITime: tciDate?.time,
        TCIZoomLink: rosterConfig.tciZoomLink,
        TCIZoomPassword: rosterConfig.tciZoomPw,
        TTFNA: rosterConfig.ttfna,
        TTDRecoveryZoomLink: rosterConfig.ttdRecoveryZoomLink,
        TTDRecoveryZoomPassword: rosterConfig.ttdRecoveryZoomPw,
        TTFDate: ttfDate?.date,
        TTFTime: ttfDate?.time,
        TTFZoomHostKey: rosterConfig.ttfHostKey,
        TTFZoomLink: rosterConfig.ttfZoomLink,
        TTFZoomPassword: rosterConfig.ttfZoomPw,
        TechTestDate: techTestDate?.date,
        TechTestNA: rosterConfig.techTestNA,
        TechTestRecoveryDate: techTestRecoveryDate?.date,
        TechTestRecoveryNA: rosterConfig.techTestRecoveryNA,
        TechTestRecoveryTime: techTestRecoveryDate?.time,
        TechTestTime: techTestDate?.time,
        TechTestZoomLink: rosterConfig.ttdZoomLink,
        TechTestZoomPassword: rosterConfig.ttdZoomPw,
        OGName: rosterConfig.ogOracle,
        TrainerName: rosterConfig.trainerOracle,
        ProductTrainingNa: rosterConfig.productTrainingNA,
      },
    };
  };

  const createRosterConfiguration = async () => {
    setLoading(true);
    dispatch(rosterConfigAction.updateProductionStatRequired(false));
    const productionStartDate = rosterConfig.productionStartDate.date;

    if (
      productionStartDate === " " ||
      productionStartDate === null ||
      productionStartDate === undefined
    ) {
      dispatch(rosterConfigAction.updateProductionStatRequired(true));
      setLoading(false);
    } else {
      let obj = preparePayloadForCreate();

      const create = await createRosterConfig(classRosterId, obj);
      if (create.message === "Success") {
        Snackhandler(true, "Saved Successfully !!!", "success");
        setLoading(false);
      } else {
        Snackhandler(true, "Failed to save !", "error");
        setLoading(false);
      }
      if (create.status === true) {
        dispatch(rosterConfigAction.updateApplyAll(false));

        if (redirectlocation?.state === "assignTrainees") {
          navigate(`/assignTrainees`, {
            state: {
              classRequestId: classRequestId,
            },
          });
        } else {
          navigate(`/rosterSearch/${classRequestId}/null`);
        }
      }
      setLoading(false);
    }
  };

  const preparePayloadForUpdate = () => {
    const {
      accDate,
      nestingDate,
      nestingEndDate,
      productTrainingDate,
      tciDate,
      tciRecoveryDate,
      techTestDate,
      techTestRecoveryDate,
      ttfDate,
    } = getrosterConfigDateTimes();
    return {
      classRequestId: classRequestId,
      wave: rosterConfig.wave,
      techEquipmentSolutionId: rosterConfig.techEquipmentSolution,
      notes: rosterConfig.notes,
      modifiedBy: localStorage.getItem("User"),
      applyEditToAllTrainees: rosterConfig.applyAll ? 1 : 0,
      classRosterDetail: {
        ACCDate: accDate?.date,
        ACCTime: accDate?.time,
        ClientSpecificZoomHostKey: rosterConfig.clientSpecificZoomHostKey,
        ClientSpecificZoomLink: rosterConfig.clientSpecificLink,
        ClientSpecificZoomPassword: rosterConfig.clientSpecificPw,
        IDMTicketNumber: rosterConfig.rosterIdm,
        NestingDate: nestingDate?.date,
        ACCNA: rosterConfig.accna,
        NestingEndDate: nestingEndDate?.date,
        NestingEndTime: nestingEndDate?.time,
        NestingNA: rosterConfig.nestingNA,
        NestingTime: nestingDate?.time,
        ProductTrainingDate: productTrainingDate?.date,
        ProductTrainingTime: productTrainingDate?.time,
        ProductTrainingZoomLink: rosterConfig.productionTrainingZoomLink,
        ProductTrainingZoomPassword: rosterConfig.productionTrainingZoomPw,
        ProductionDate: rosterConfig.productionStartDate.date,
        SRWDownloadDueDate: rosterConfig.srw.date,
        SRWDownloadDueTime: rosterConfig.srw.time,
        TCIDate: tciDate?.date,
        TCINA: rosterConfig.tcina,
        TCIRecoveryDate: tciRecoveryDate?.date,
        TCIRecoveryNA: rosterConfig.tciRecoveryNA,
        TCIRecoveryTime: tciRecoveryDate?.time,
        TCIRecoveryZoomLink: rosterConfig.tciRecoveryZoomLink,
        TCIRecoveryZoomPassword: rosterConfig.tciRecoveryZoomPw,
        TCITime: tciDate?.time,
        TCIZoomLink: rosterConfig.tciZoomLink,
        TCIZoomPassword: rosterConfig.tciZoomPw,
        TTFNA: rosterConfig.ttfna,
        TTDRecoveryZoomLink: rosterConfig.ttdRecoveryZoomLink,
        TTDRecoveryZoomPassword: rosterConfig.ttdRecoveryZoomPw,
        TTFDate: ttfDate?.date,
        TTFTime: ttfDate?.time,
        TTFZoomHostKey: rosterConfig.ttfHostKey,
        TTFZoomLink: rosterConfig.ttfZoomLink,
        TTFZoomPassword: rosterConfig.ttfZoomPw,
        TechTestDate: techTestDate?.date,
        TechTestNA: rosterConfig.techTestNA,
        TechTestRecoveryDate: techTestRecoveryDate?.date,
        TechTestRecoveryNA: rosterConfig.techTestRecoveryNA,
        TechTestRecoveryTime: techTestRecoveryDate?.time,
        TechTestTime: techTestDate?.time,
        TechTestZoomLink: rosterConfig.ttdZoomLink,
        TechTestZoomPassword: rosterConfig.ttdZoomPw,
        OGName: rosterConfig.ogOracle,
        TrainerName: rosterConfig.trainerOracle,
        ProductTrainingNa: rosterConfig.productTrainingNA,
        NetworkTypeId: rosterConfig.networkType.toString(),
        DistributionTypeId: rosterConfig.distributionType.toString(),
      },
    };
  };

  const handleRosterConifg = async () => {
    setLoading(true);
    dispatch(rosterConfigAction.updateProductionStatRequired(false));
    if (
      rosterConfig.productionStartDate.date === " " ||
      rosterConfig.productionStartDate.date === null
    ) {
      dispatch(rosterConfigAction.updateProductionStatRequired(true));
      setLoading(false);
    } else {
      let obj = preparePayloadForUpdate();

      const update = await updateRosterConfiguration(classRosterId, obj);
      if (update.message == "Success") {
        Snackhandler(true, "Updated Successfully !!!", "success");
        setLoading(false);
      } else {
        Snackhandler(true, "Failed to save !", "error");
        setLoading(false);
      }
      if (update.status === true) {
        dispatch(rosterConfigAction.updateApplyAll(false));
        navigate(
          classRequestIdStore.traineeSearch
            ? `/rosterSearch/null/${classRosterId}`
            : `/rosterSearch/${classRequestId}/null`,
        );
      }
      setLoading(false);
    }
  };

  const resetConfigHandler = useCallback(async () => {
    setLoading(true);

    const configRosterDetails = await getRosterConfig(
      classRosterId,
      location[5],
    );
    if (configRosterDetails.status === true) {
      dispatchClassRosterData(configRosterDetails.data[0]);
    }
    setType(typeParam);
  }, [classRosterId, typeParam]);

  const dispatchClientSpecifics = (classRosterData) => {
    dispatch(
      rosterConfigAction.clientSpecificLink(
        classRosterData.clientSpecificZoomLink,
      ),
    );
    dispatch(
      rosterConfigAction.clientSpecificPw(
        classRosterData.clientSpecificZoomPassword,
      ),
    );
    dispatch(
      rosterConfigAction.clientSpecificZoomHostKey(
        classRosterData.clientSpecificZoomHostKey,
      ),
    );
  };

  const dispatchOGData = (classRosterData) => {
    dispatch(rosterConfigAction.ogEmail(classRosterData.ogEmail));
    dispatch(rosterConfigAction.ogName(classRosterData.ogName));
    dispatch(rosterConfigAction.updateOgOracle(classRosterData.ogOracleId));
    dispatch(rosterConfigAction.ogName(classRosterData.ogName));
  };

  const dispatchTciData = (classRosterData) => {
    dispatch(
      rosterConfigAction.tciDate({
        date: classRosterData.tciDate,
        time: classRosterData.tciTime,
      }),
    );
    dispatch(
      rosterConfigAction.tciRecoveryDate({
        date: classRosterData.tciRecoveryDate,
        time: classRosterData.tciRecoveryTime,
      }),
    );
    dispatch(
      rosterConfigAction.tciRecoveryNA(
        classRosterData.tciRecoveryNA ? classRosterData.tciRecoveryNA : false,
      ),
    );
    dispatch(
      rosterConfigAction.tciRecoveryZoomLink(
        classRosterData.tciRecoveryZoomLink,
      ),
    );
    dispatch(
      rosterConfigAction.tciRecoveryZoomPw(
        classRosterData.tciRecoveryZoomPassword,
      ),
    );
    dispatch(rosterConfigAction.tciZoomLink(classRosterData.tciZoomLink));
    dispatch(rosterConfigAction.tciZoomPw(classRosterData.tciZoomPassword));
    dispatch(
      rosterConfigAction.tcina(
        classRosterData.tcina ? classRosterData.tcina : false,
      ),
    );
  };

  const dispatchTpcData = (classRosterData) => {
    dispatch(
      rosterConfigAction.tpcDate({
        date: classRosterData.tpcDate,
        time: classRosterData.tpcTime,
      }),
    );
    dispatch(rosterConfigAction.TPCZoomHostKey(classRosterData.tpcZoomHostKey));
    dispatch(rosterConfigAction.tpcZoomLink(classRosterData.tpcZoomLink));
    dispatch(rosterConfigAction.tpcZoomPw(classRosterData.tpcZoomPassword));
    dispatch(
      rosterConfigAction.tpcna(
        classRosterData.tpcna ? classRosterData.tpcna : false,
      ),
    );
    dispatch(rosterConfigAction.tpcHostEmail(classRosterData.tpcHostEmail));
    dispatch(rosterConfigAction.tpcHostName(classRosterData.tpcHostName));
    dispatch(
      rosterConfigAction.tpcHostOracleId(classRosterData.tpcHostOracleId),
    );
  };

  const dispatchTtdData = (classRosterData) => {
    dispatch(
      rosterConfigAction.ttdBotDate({
        date: classRosterData.techTestDate,
        time: classRosterData.techTestTime,
      }),
    );
    dispatch(
      rosterConfigAction.ttdRecoveryDate({
        date: classRosterData.techTestRecoveryDate,
        time: classRosterData.techTestRecoveryTime,
      }),
    );
    dispatch(
      rosterConfigAction.ttdRecoveryZoomLink(
        classRosterData.ttdRecoveryZoomLink,
      ),
    );
    dispatch(
      rosterConfigAction.ttdRecoveryZoomPw(
        classRosterData.ttdRecoveryZoomPassword,
      ),
    );
    dispatch(rosterConfigAction.ttdZoomLink(classRosterData.techTestZoomLink));
    dispatch(
      rosterConfigAction.ttdZoomPw(classRosterData.techTestZoomPassword),
    );
  };

  const dispatchTtfData = (classRosterData) => {
    dispatch(
      rosterConfigAction.ttfDate({
        date: classRosterData.ttfDate,
        time: classRosterData.ttfTime,
      }),
    );
    dispatch(rosterConfigAction.ttfHostKey(classRosterData.ttfZoomHostKey));
    dispatch(rosterConfigAction.ttfZoomLink(classRosterData.ttfZoomLink));
    dispatch(rosterConfigAction.ttfZoomPw(classRosterData.ttfZoomPassword));
    dispatch(
      rosterConfigAction.ttfna(
        classRosterData.ttfna ? classRosterData.ttfna : false,
      ),
    );
  };

  const dispatchClassRosterData = useCallback(
    (classRosterData) => {
      if (classRosterData) {
        dispatch(rosterConfigAction.updateProductionStatRequired(false));
        dispatch(
          rosterConfigAction.accDate({
            date: classRosterData.accDate,
            time: classRosterData.accTime,
          }),
        );
        dispatch(
          rosterConfigAction.accna(
            classRosterData.accna ? classRosterData.accna : false,
          ),
        );
        dispatchClientSpecifics(classRosterData);
        dispatch(rosterConfigAction.modifiedBy(classRosterData.modifiedBy));
        dispatch(
          rosterConfigAction.nestingDate({
            date: classRosterData.nestingDate,
            time: classRosterData.nestingTime,
          }),
        );
        dispatch(
          rosterConfigAction.nestingEndDate({
            date: classRosterData.nestingEndDate,
            time: classRosterData.nestingEndTime,
          }),
        );
        dispatch(rosterConfigAction.notes(classRosterData.rosterNotes));
        dispatchOGData(classRosterData);
        dispatch(
          rosterConfigAction.productTrainingNa(
            classRosterData.productTrainingNA,
          ),
        );
        dispatch(
          rosterConfigAction.productTrainingDate({
            date: classRosterData.productTrainingDate,
            time: classRosterData.productTrainingTime,
          }),
        );
        dispatch(
          rosterConfigAction.productionStartDate({
            date: classRosterData.productionDate,
          }),
        );
        dispatch(
          rosterConfigAction.productionTrainingZoomLink(
            classRosterData.productTrainingZoomLink,
          ),
        );
        dispatch(
          rosterConfigAction.productionTrainingZoomPw(
            classRosterData.productTrainingZoomPassword,
          ),
        );

        dispatch(
          rosterConfigAction.srw({
            date: classRosterData.srwDownloadDueDate,
            time: classRosterData.srwDownloadDueTime,
          }),
        );
        dispatchTciData(classRosterData);
        dispatch(
          rosterConfigAction.nestingNA(
            classRosterData.nestingNA ? classRosterData.nestingNA : false,
          ),
        );
        dispatch(
          rosterConfigAction.techEquipmentSolution(
            classRosterData.techEquipmentSolutionId,
          ),
        );

        dispatch(rosterConfigAction.askNowTDT(classRosterData.askNowTDT));

        dispatch(
          rosterConfigAction.updateDistributionType(
            classRosterData.distributionTypeId,
          ),
        );

        dispatch(
          rosterConfigAction.updateNetworkType(classRosterData.networkTypeId),
        );

        dispatch(
          rosterConfigAction.techTestNA(
            classRosterData.techTestNA ? classRosterData.techTestNA : false,
          ),
        );

        dispatch(
          rosterConfigAction.techTestRecoveryNA(
            classRosterData.techTestRecoveryNA
              ? classRosterData.techTestRecoveryNA
              : false,
          ),
        );
        dispatchTpcData(classRosterData);
        dispatchTtdData(classRosterData);
        dispatchTtfData(classRosterData);
        dispatch(rosterConfigAction.wave(classRosterData.wave));

        if (classRosterData.trainerName === null) {
          dispatch(rosterConfigAction.traineeName(" "));
        } else {
          dispatch(rosterConfigAction.traineeName(classRosterData.trainerName));
        }

        dispatch(rosterConfigAction.traineeEmail(classRosterData.trainerEmail));
        dispatch(
          rosterConfigAction.updateTrainerOracle(
            classRosterData.trainerOracleId,
          ),
        );
        dispatch(
          rosterConfigAction.updateTcpOracle(classRosterData.tpcHostOracleId),
        );
        dispatch(
          rosterConfigAction.updateModfiedDate(classRosterData.modifiedDate),
        );
        dispatch(
          rosterConfigAction.updateRosterLockDate(
            classRosterData.rosterLockDate,
          ),
        );
        dispatch(
          rosterConfigAction.updateAssignRosterLockDate(
            classRosterData.assignTraineesLockTimestamp,
          ),
        );

        dispatch(
          rosterConfigAction.istentativettfdate(
            classRosterData.istentativettfdate,
          ),
        );
        dispatch(
          rosterConfigAction.istentativeproducttrainingdate(
            classRosterData.istentativeproducttrainingdate,
          ),
        );
        dispatch(
          rosterConfigAction.istentativenestingdate(
            classRosterData.istentativenestingdate,
          ),
        );
        setDataLoaded(true);
        setLoading(false);
      }
    },
    [dataLoaded, dispatch],
  );

  const handleCancel = () => {
    if (redirectlocation?.state === "assignTrainees") {
      navigate(`/assignTrainees`, {
        state: {
          classRequestId: classRequestId,
        },
      });
    } else if (redirectlocation?.state === "createIDMTicket") {
      navigate(-1);
    } else {
      navigate(
        classRequestIdStore.traineeSearch
          ? `/rosterSearch/null/${classRosterId}`
          : `/rosterSearch/${classRequestId}/null`,
      );
    }
  };

  return (
    <div>
      {loading ? <BackdropComponent /> : null}

      <Grid className="background-white" container>
        <Grid className={"flex-space-between p-10-20 align-center"} item xs={6}>
          <div>
            <div className="fs-1-5">
              {t("rosterConfiguration")}
              {type === "clone" && <span>-{t("new")}</span>}
            </div>
            <div className={"font-style-italic redColor"}>{t("note")}</div>
          </div>
          {type !== "clone" && (
            <div>
              <span>
                {" "}
                <Checkbox
                  checked={rosterConfig.applyAll}
                  id="applyAll"
                  name="applyAll"
                  onChange={(e) => {
                    dispatch(
                      rosterConfigAction.updateApplyAll(e.target.checked),
                    );
                  }}
                />
                <label htmlFor="applyAll"> {t("apply")}</label>
              </span>
            </div>
          )}
        </Grid>
        <Grid className={"only-flex"} item xs={6}>
          <div className={`flex-display  m-20px`}>
            <div className={"p-10-20  yellow"}>OG</div>
            <div className={"p-10-20  blue m-l-5 "}>L&D</div>
            <div className={"p-10-20  green m-l-5"}>WFM</div>
            <div className={"p-10-20 pink m-l-5"}>TA</div>
          </div>
          <div className="flex-display m-20px">
            <AsttButton onClick={resetConfigHandler}>{t("reset")}</AsttButton>
          </div>
          <div className="flex-display align-center m-20px">
            <CloseIcon onClick={handleCancel} />
          </div>
        </Grid>
      </Grid>
      <Grid
        className={`overfolow-scroll background-grey ${classes.rosterConfigHeight}`}
        container
      >
        <Grid className={classes.keyData} item xs={6}>
          {!loading && <Inputs rosterID={classRosterId} type={type} />}
        </Grid>
        <Grid className={classes.keyData} item xs={6}>
          {!loading && (
            <TraineeDetails
              rosterID={classRosterId}
              classRequestId={classRequestId}
              type={type}
            />
          )}
        </Grid>
      </Grid>
      <div className={`${classes.buttonContainer} flex-center`}>
        {type == "clone" ? (
          <div className="info-section">
            {" "}
            <AsttButton onClick={(e) => createRosterConfiguration()}>
              {t("Add")}
            </AsttButton>
          </div>
        ) : (
          <div className="info-section">
            {" "}
            <AsttButton onClick={(e) => handleRosterConifg()}>
              {t("update")}
            </AsttButton>
          </div>
        )}
        <div className="info-section">
          {" "}
          <AsttButtonLight onClick={() => handleCancel()}>
            {t("back")}
          </AsttButtonLight>
        </div>
      </div>
    </div>
  );
};

export default RosterConfig;
